/**
 * Ensure Navigation Index — cache warm / invalidation.
 *
 * Checks cache freshness via source file mtime comparison. If stale,
 * calls buildNavigationContext() and writes to .claude/cache/.
 * If fresh, returns cached data immediately.
 *
 * This is the performance-critical path for SessionStart hooks.
 */

import { stat } from "node:fs/promises";
import { buildNavigationContext, type NavigationContextOptions } from "../navigation-context.js";
import {
  createCacheStore,
  computeSourceHash,
  type CacheStore,
} from "./cache-store.js";
import { createExclusiveLock } from "./latency.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EnsureNavigationIndexOptions {
  /** Absolute path to .claude/cache/ directory. */
  cacheDir: string;
  /** Paths to source files whose mtime drives cache invalidation. */
  sourceFiles: string[];
  /** Options for buildNavigationContext() when cache is stale. */
  navigationOptions: NavigationContextOptions;
}

export interface NavigationIndexResult {
  navigationIndex: Record<string, unknown>;
  /** Whether the result was loaded from cache (true) or freshly built (false). */
  fromCache: boolean;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

const CACHE_KEY = "navigation-index";

/** Shared lock to prevent concurrent navigation-index builds. */
const buildLock = createExclusiveLock<NavigationIndexResult>();

/**
 * Collect mtimes for source files and produce a cache hash.
 */
async function computeCurrentHash(sourceFiles: string[]): Promise<string> {
  const mtimes: Array<string | number> = [];
  for (const file of sourceFiles) {
    try {
      const s = await stat(file);
      mtimes.push(s.mtimeMs);
    } catch {
      // File not found — include a sentinel so hash changes if file appears
      mtimes.push(`missing:${file}`);
    }
  }
  return computeSourceHash(mtimes);
}

/**
 * Ensure the navigation index is available and fresh. Returns the index
 * data, building + caching if necessary.
 */
export async function ensureNavigationIndex(
  options: EnsureNavigationIndexOptions,
): Promise<NavigationIndexResult> {
  return buildLock.run(async () => {
    const store: CacheStore = createCacheStore({ cacheDir: options.cacheDir });
    const currentHash = await computeCurrentHash(options.sourceFiles);

    // Try cache first
    const cached = await store.readIfFresh<Record<string, unknown>>(
      CACHE_KEY,
      currentHash,
    );
    if (cached) {
      return { navigationIndex: cached, fromCache: true };
    }

    // Cache miss or stale — rebuild
    const context = await buildNavigationContext(options.navigationOptions);
    const navigationIndex = context.navigationIndex;

    // Write to cache (non-blocking error handling)
    try {
      await store.write(CACHE_KEY, navigationIndex, currentHash);
    } catch {
      // Cache write failure is non-fatal
    }

    return { navigationIndex, fromCache: false };
  });
}
